import { useState, useEffect, useCallback } from "react";
import { db, doc, onSnapshot, setDoc, updateDoc, arrayUnion, arrayRemove } from "../lib/firebase";
import { useAuthStore } from "../stores/authStore";
import type { FavoriteSong } from "../types";

export function useFavorites() {
  const { user } = useAuthStore();
  const [favorites, setFavorites] = useState<FavoriteSong[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!user) {
      setFavorites([]);
      setLoading(false);
      return;
    }

    const playlistRef = doc(db, "personalPlaylists", user.uid);
    const unsubscribe = onSnapshot(playlistRef, (docSnap) => {
      if (docSnap.exists()) {
        const rawSongs = docSnap.data()?.songs || [];
        const formatted: FavoriteSong[] = rawSongs.map((s: any) => ({
          videoId: s.id?.videoId || s.videoId,
          title: s.snippet?.title || s.title || "Unknown Title",
          channelTitle: s.snippet?.channelTitle || s.channelTitle || "Karaoke",
          thumbnailUrl: s.snippet?.thumbnails?.default?.url || s.thumbnailUrl || "",
          embeddable: s.status?.embeddable ?? true,
          addedAt: s.addedAt || Date.now(),
        }));
        setFavorites(formatted);
      } else {
        setFavorites([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const addFavorite = useCallback(async (song: any) => {
    if (!user) return;
    const playlistRef = doc(db, "personalPlaylists", user.uid);
    const favoriteObject = {
      id: { videoId: song.videoId || song.id?.videoId },
      videoId: song.videoId || song.id?.videoId,
      snippet: {
        title: song.title || song.snippet?.title,
        channelTitle: song.channelTitle || song.snippet?.channelTitle,
        thumbnails: {
          default: { url: song.thumbnailUrl || song.snippet?.thumbnails?.default?.url || "" }
        }
      },
      status: {
        embeddable: song.embeddable ?? song.status?.embeddable ?? true
      },
      addedAt: Date.now()
    };

    await setDoc(playlistRef, {
      songs: arrayUnion(favoriteObject)
    }, { merge: true });
  }, [user]);

  const removeFavorite = useCallback(async (videoId: string) => {
    if (!user) return;
    const playlistRef = doc(db, "personalPlaylists", user.uid);
    const songToRemove = favorites.find((s) => s.videoId === videoId);
    if (!songToRemove) return;

    // Find the exact item to remove from the array
    const rawSong = {
      id: { videoId: songToRemove.videoId },
      videoId: songToRemove.videoId,
      snippet: {
        title: songToRemove.title,
        channelTitle: songToRemove.channelTitle,
        thumbnails: {
          default: { url: songToRemove.thumbnailUrl }
        }
      },
      status: {
        embeddable: songToRemove.embeddable ?? true
      },
      addedAt: songToRemove.addedAt
    };

    try {
      await updateDoc(playlistRef, {
        songs: arrayRemove(rawSong)
      });
    } catch {
      // Fallback: set filtered songs array
      const updated = favorites.filter((s) => s.videoId !== videoId);
      await setDoc(playlistRef, { songs: updated }, { merge: true });
    }
  }, [user, favorites]);

  const isFavorite = useCallback((videoId: string) => {
    return favorites.some((s) => s.videoId === videoId);
  }, [favorites]);

  return { favorites, loading, addFavorite, removeFavorite, isFavorite };
}
